import type { Block, Task, Settings, GoogleCalendar, ActivityWindow, ActivitySummary } from '@focus/shared';

const BASE = '/api';

async function j<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { ...(init?.headers as any || {}) };
  if (init?.body != null && !('Content-Type' in headers) && !('content-type' in headers)) {
    headers['Content-Type'] = 'application/json';
  }
  const r = await fetch(BASE + path, { ...init, headers });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText} on ${path}`);
  if (r.status === 204) return undefined as unknown as T;
  const ct = r.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return undefined as unknown as T;
  return (await r.json()) as T;
}

export const api = {
  health: () => j<{ ok: boolean; env: string; gcalEnabled: boolean }>('/health'),

  // tasks
  listTasks: () => j<Task[]>('/tasks'),
  createTask: (t: Partial<Task> & { title: string }) =>
    j<Task>('/tasks', { method: 'POST', body: JSON.stringify(t) }),
  patchTask: (id: string, patch: Partial<Task>) =>
    j<Task>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteTask: (id: string) => j<{ ok: true }>(`/tasks/${id}`, { method: 'DELETE' }),
  toggleTaskDone: (id: string) => j<Task>(`/tasks/${id}/toggle-done`, { method: 'POST' }),
  reorderTasks: (orderedIds: string[]) =>
    j<{ ok: true }>('/tasks/reorder', { method: 'POST', body: JSON.stringify({ orderedIds }) }),
  quickTask: (payload: { title?: string; tasks?: any[] } | string) =>
    j<{ ok: true; created: Task[] }>('/tasks/quick', {
      method: 'POST',
      body: typeof payload === 'string' ? JSON.stringify({ title: payload }) : JSON.stringify(payload),
    }),
  retagTasks: (from: string, to: string) =>
    j<{ ok: true; count: number }>('/tasks/retag', {
      method: 'POST',
      body: JSON.stringify({ from, to }),
    }),
  deleteTasksByTag: (tag: string) =>
    j<{ ok: true; count: number }>('/tasks/by-tag/delete', {
      method: 'POST',
      body: JSON.stringify({ tag }),
    }),

  // blocks
  listBlocks: (weekStart: number) => j<Block[]>(`/blocks?weekStart=${weekStart}`),
  createBlock: (b: Omit<Block, 'id' | 'createdAt'> & { id?: string }) =>
    j<Block>('/blocks', { method: 'POST', body: JSON.stringify(b) }),
  patchBlock: (id: string, patch: Partial<Block>) =>
    j<Block>(`/blocks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteBlock: (id: string) => j<{ ok: true }>(`/blocks/${id}`, { method: 'DELETE' }),
  cascadeShift: (weekStart: number, dayIdx: number, fromS: number, deltaHours: number) =>
    j<{ ok: true; shiftedCount: number }>('/blocks/cascade-shift', {
      method: 'POST',
      body: JSON.stringify({ weekStart, dayIdx, fromS, deltaHours }),
    }),

  // settings
  getSettings: () => j<Settings>('/settings'),
  putSettings: (s: Partial<Settings>) =>
    j<Settings>('/settings', { method: 'PATCH', body: JSON.stringify(s) }),

  // google
  googleStatus: () =>
    j<{
      configured: boolean;
      connected: boolean;
      clientId: string;
      redirectUri: string;
      accountEmail: string | null;
      lastSync: number | null;
    }>('/auth/google/status'),
  googleStartUrl: () => BASE + '/auth/google/start',
  googleDisconnect: () => j<{ ok: true }>('/auth/google/disconnect', { method: 'POST' }),
  gcalCalendars: () => j<GoogleCalendar[]>('/gcal/calendars'),
  gcalPatch: (id: string, patch: { selected: boolean }) =>
    j<{ ok: true }>(`/gcal/calendars/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  gcalSync: () =>
    j<{ ok: true; calendars: number; imported: number }>('/gcal/sync', { method: 'POST' }),
  gcalPush: (blockId: string, calendarId: string) =>
    j<{ ok: boolean; gcalEventId: string | null }>(`/gcal/push/${encodeURIComponent(blockId)}`, {
      method: 'POST',
      body: JSON.stringify({ calendarId }),
    }),

  // activity (Ist-Ansicht)
  getActivityWindows: (params: { weekStart: number; dayIdx?: number; bucketMin?: number }) => {
    const q = new URLSearchParams();
    q.set('weekStart', String(params.weekStart));
    if (params.dayIdx != null) q.set('dayIdx', String(params.dayIdx));
    if (params.bucketMin != null) q.set('bucketMin', String(params.bucketMin));
    return j<{ weekStart: number; bucketMin: number; days: Record<number, ActivityWindow[]> }>(`/activity/windows?${q}`);
  },
  getActivityLatest: () =>
    j<{ ts: number; app: string; title: string | null; category: string | null } | null>('/activity/latest'),
  getActivitySummary: (params: { from: number; to: number }) => {
    const q = new URLSearchParams();
    q.set('from', String(params.from));
    q.set('to', String(params.to));
    return j<ActivitySummary>(`/activity/summary?${q}`);
  },

  // sleep (Eight Sleep)
  getSleepRange: (from: number, to: number) =>
    j<Array<{ id: string; startTs: number; endTs: number | null; score: number | null; stages: any }>>(
      `/sleep/range?from=${from}&to=${to}`,
    ),
  getSleepLatest: () =>
    j<{ id: string; startTs: number; endTs: number | null; score: number | null; stages: any; updatedAt: number } | null>('/sleep/latest'),
  syncSleep: () => j<{ ok: true; synced: number }>('/sleep/sync', { method: 'POST' }),

  // stream (SSE)
  stream: () => new EventSource(BASE + '/stream'),
};
